import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { AdminStats, User, TemplateItem } from '@/lib/types'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { AnalyticsDashboard } from '@/components/AnalyticsDashboard'
import {
  Settings,
  Users,
  Shield,
  X,
  BarChart3,
  Zap,
  CreditCard,
  User as UserIcon,
  HelpCircle,
  Info,
  RefreshCw,
  Clock,
  Mail,
  CheckCircle2,
  XCircle,
  Calendar,
  Activity,
  Search,
  Filter,
  Edit,
  Crown
} from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

// Bar Chart Component for Model Usage
function BarChart({ data }: { data: Array<{ model_id: string; model_label: string; usage_count: number }> }) {
  if (!data || data.length === 0) {
    return <div className="text-center py-8 text-gray-500">No data available</div>
  }

  const maxValue = Math.max(...data.map(d => d.usage_count), 1)
  const width = 800
  const height = 400
  const padding = { top: 20, right: 20, bottom: 80, left: 60 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom
  const barWidth = chartWidth / data.length * 0.8
  const barSpacing = chartWidth / data.length * 0.2

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} className="w-full" viewBox={`0 0 ${width} ${height}`}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + chartHeight - (ratio * chartHeight)
          const value = Math.round(maxValue * ratio)
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="4,4"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                textAnchor="end"
                fontSize="12"
                fill="#6b7280"
              >
                {value.toLocaleString()}
              </text>
            </g>
          )
        })}

        {/* Bars */}
        {data.map((item, index) => {
          const barHeight = (item.usage_count / maxValue) * chartHeight
          const x = padding.left + index * (barWidth + barSpacing) + barSpacing / 2
          const y = padding.top + chartHeight - barHeight
          
          // Color based on rank
          const color = index === 0 ? '#fbbf24' : index === 1 ? '#9ca3af' : index === 2 ? '#fb923c' : '#3b82f6'
          
          return (
            <g key={item.model_id}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={color}
                className="hover:opacity-80 transition-opacity"
              >
                <title>{`${item.model_label}: ${item.usage_count.toLocaleString()} messages`}</title>
              </rect>
              
              {/* Value label on top of bar */}
              {barHeight > 20 && (
                <text
                  x={x + barWidth / 2}
                  y={y - 5}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#374151"
                  fontWeight="500"
                >
                  {item.usage_count.toLocaleString()}
                </text>
              )}
              
              {/* Model label below bar */}
              <text
                x={x + barWidth / 2}
                y={height - padding.bottom + 15}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
                transform={`rotate(-45 ${x + barWidth / 2} ${height - padding.bottom + 15})`}
              >
                {item.model_label.length > 15 ? item.model_label.substring(0, 15) + '...' : item.model_label}
              </text>
            </g>
          )
        })}

        {/* Y-axis line */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={height - padding.bottom}
          stroke="#374151"
          strokeWidth="2"
        />

        {/* X-axis line */}
        <line
          x1={padding.left}
          y1={height - padding.bottom}
          x2={width - padding.right}
          y2={height - padding.bottom}
          stroke="#374151"
          strokeWidth="2"
        />
      </svg>
    </div>
  )
}

interface AdminModalProps {
  isOpen: boolean
  onClose: () => void
  currentUser: User | null
}

const sidebarItems = [
  { id: 'my-account', label: 'My account', icon: UserIcon },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'people', label: 'Sign-in Logs', icon: Users },
  { id: 'popular-templates', label: 'Popular Templates', icon: Crown },
  { id: 'popular-models', label: 'Popular Models', icon: BarChart3 },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'integrations', label: 'Integrations', icon: Zap },
]

export function AdminModal({ isOpen, onClose, currentUser }: AdminModalProps) {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [recentSessions, setRecentSessions] = useState<any[]>([])
  const [templates, setTemplates] = useState<TemplateItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('my-account')
  const [isUpdating, setIsUpdating] = useState(false)
  const [nameFilter, setNameFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [sortBy, setSortBy] = useState<'name' | 'last_activity' | 'email'>('last_activity')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const { toast } = useToast()

  // Popular Models state
  const [popularModels, setPopularModels] = useState<Array<{ model_id: string; model_label: string; usage_count: number }>>([])
  const [modelsLoading, setModelsLoading] = useState(false)
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'custom'>('30d')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')

  // Helper function to determine if user is currently active (within last 2 weeks)
  const isUserCurrentlyActive = (lastActivityTime: string | null) => {
    if (!lastActivityTime) return false
    const lastActivity = new Date(lastActivityTime)
    const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) // 2 weeks ago
    return lastActivity > twoWeeksAgo
  }

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      const [statsData, usersResponse, recentSessionsResponse, publicTemplatesResponse] = await Promise.all([
        apiClient.getAdminStats(),
        apiClient.getAdminUsers(),
        apiClient.getAdminRecentSessions(200),
        apiClient.listTemplates('public', '', 1, 100) // Fetch up to 100 public templates
      ])

      setStats(statsData)

      // Debug: Log the response to understand the structure
      console.log('Users API response:', usersResponse)

      // Extract users array from the response (backend returns {users: [], total, skip, limit})
      const usersData = usersResponse?.users || []

      // Process and display all users from the database with proper mapping
      const processedUsers = usersData.map(user => {
        // Try to get email from multiple possible fields in the user object
        const userEmail = user.email ||
                         user.preferred_username ||
                         user.unique_name ||
                         user.upn ||
                         user.mail ||
                         user.userPrincipalName ||
                         'No email'

        return {
          id: user.id,
          email: userEmail,
          name: user.name || 'Unknown User',
          is_active: user.is_active, // Account enabled/disabled (not "recent activity")
          is_admin: user.is_admin === true,
          access_level: user.is_admin ? 'Admin' : 'User',
          created_at: user.created_at,
          updated_at: user.updated_at,
          last_sign_in: user.last_sign_in_at || null,
          last_sign_in_at: user.last_sign_in_at || null,
          last_activity_at: user.last_activity_at || null,
        }
      })

      setUsers(processedUsers)
      setRecentSessions(recentSessionsResponse?.sessions || [])

      // Process templates - only public templates sorted by usage count
      const publicTemplatesData = publicTemplatesResponse?.items || []

      // Sort templates by usage count in descending order (most used first)
      const sortedTemplates = publicTemplatesData
        .filter(template => template.visibility === 'public')
        .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))

      console.log('Sorted popular templates:', sortedTemplates)
      setTemplates(sortedTemplates)

    } catch (error) {
      console.error('Failed to load admin data:', error)
      toast({
        title: "Error",
        description: "Failed to load admin data",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  useEffect(() => {
    if (isOpen) {
      loadData()
    }
  }, [isOpen, loadData])

  // Load popular models data
  const loadPopularModels = useCallback(async () => {
    try {
      setModelsLoading(true)
      
      // Calculate date range
      let startDate: string | undefined
      let endDate: string | undefined
      
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      endDate = end.toISOString().split('T')[0]
      
      if (dateRange === 'custom') {
        if (customStartDate && customEndDate) {
          startDate = customStartDate
          endDate = customEndDate
        } else {
          // Default to 30 days if custom dates not set
          const start = new Date()
          start.setDate(start.getDate() - 30)
          start.setHours(0, 0, 0, 0)
          startDate = start.toISOString().split('T')[0]
        }
      } else {
        const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90
        const start = new Date()
        start.setDate(start.getDate() - days)
        start.setHours(0, 0, 0, 0)
        startDate = start.toISOString().split('T')[0]
      }
      
      const response = await apiClient.getPopularModels(startDate, endDate)
      setPopularModels(response.models)
    } catch (error) {
      console.error('Failed to load popular models:', error)
      toast({
        title: "Error",
        description: "Failed to load popular models data",
        variant: "destructive",
      })
    } finally {
      setModelsLoading(false)
    }
  }, [dateRange, customStartDate, customEndDate, toast])

  useEffect(() => {
    if (isOpen && activeTab === 'popular-models') {
      loadPopularModels()
    }
  }, [isOpen, activeTab, loadPopularModels])

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      setIsUpdating(true)
      const updatedUser = await apiClient.toggleUserAdmin(userId)

      // Update local state with the actual response from server
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, is_admin: updatedUser.is_admin, access_level: updatedUser.is_admin ? 'Admin' : 'User' }
          : user
      ))

      toast({
        title: "Success",
        description: `User role updated to ${updatedUser.is_admin ? 'Admin' : 'User'}`,
      })
    } catch (error: any) {
      console.error('Failed to update user role:', error)
      toast({
        title: "Error",
        description: error?.message || "Failed to update user role",
        variant: "destructive",
      })
    } finally {
      setIsUpdating(false)
    }
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[1000px] max-h-[90vh] overflow-hidden p-0 relative">
        {/* Fixed action buttons in upper right */}
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={loadData}
            disabled={isLoading}
            className="bg-white shadow-sm border hover:bg-gray-50"
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin text-blue-500' : 'text-gray-600'}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="bg-white shadow-sm border hover:bg-gray-50"
            title="Close admin portal"
          >
            <X className="h-4 w-4 text-gray-600" />
          </Button>
        </div>
        <div className="flex h-[80vh]">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Admin Portal
              </DialogTitle>
            </div>

            <nav className="flex-1 p-4 space-y-1">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    "flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors",
                    activeTab === item.id
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  <item.icon className="mr-3 h-4 w-4" />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-auto bg-white">
            <div className="p-6 h-full">

              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-lg">Loading...</div>
                </div>
              ) : (
                <>
                  {/* Users Tab */}
                  {activeTab === 'users' && (
                    <div className="space-y-6">
                      {/* Enhanced Header Section */}
                      <div className="pr-20">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <Users className="h-6 w-6 text-blue-600" />
                            User Management
                          </h1>
                          <p className="text-gray-600 text-sm mt-1">Manage user accounts and access levels</p>
                        </div>
                      </div>

                      {/* Enhanced Stats Cards */}
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Users className="h-4 w-4 text-blue-600" />
                                  <span className="text-xs font-semibold text-blue-800">Total Users</span>
                                </div>
                                <p className="text-2xl font-bold text-blue-900">{users.length}</p>
                              </div>
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <Users className="h-4 w-4 text-blue-600" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Crown className="h-4 w-4 text-purple-600" />
                                  <span className="text-xs font-semibold text-purple-800">Admins</span>
                                </div>
                                <p className="text-2xl font-bold text-purple-900">
                                  {users.filter(u => u.is_admin).length}
                                </p>
                              </div>
                              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                <Crown className="h-4 w-4 text-purple-600" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  <span className="text-xs font-semibold text-green-800">Active Users</span>
                                </div>
                                <p className="text-2xl font-bold text-green-900">
                                  {users.filter(u => isUserCurrentlyActive(u.last_activity_at || null)).length}
                                </p>
                              </div>
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Compact Filters Section */}
                      <Card className="bg-gray-50 border-gray-200">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Filter className="h-4 w-4 text-gray-600" />
                            <h3 className="text-base font-semibold text-gray-900">Filters</h3>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search by name..."
                                value={nameFilter}
                                onChange={(e) => setNameFilter(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                              />
                            </div>
                            <div className="relative">
                              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none"
                              >
                                <option value="all">All Roles</option>
                                <option value="admin">👑 Admins</option>
                                <option value="user">👤 Users</option>
                              </select>
                            </div>
                          </div>
                          {(nameFilter || roleFilter !== 'all') && (
                            <div className="mt-3 flex items-center gap-2">
                              {nameFilter && (
                                <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                                  "{nameFilter}"
                                </Badge>
                              )}
                              {roleFilter !== 'all' && (
                                <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs">
                                  {roleFilter === 'admin' ? 'Admins' : 'Users'}
                                </Badge>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Compact Users Table */}
                      <Card className="shadow-sm">
                        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-gray-600" />
                              <div>
                                <CardTitle className="text-lg text-gray-900">User Directory</CardTitle>
                                <CardDescription className="text-xs">
                                  {users
                                    .filter(user => {
                                      const name = (user.name || 'Unknown User').toLowerCase()
                                      const nameMatch = nameFilter === '' || name.includes(nameFilter.toLowerCase())
                                      const roleMatch = roleFilter === 'all' ||
                                        (roleFilter === 'admin' && user.is_admin) ||
                                        (roleFilter === 'user' && !user.is_admin)
                                      return nameMatch && roleMatch
                                    }).length
                                  } of {users.length} users
                                </CardDescription>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-0">
                          <div className="max-h-80 overflow-y-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-50/50">
                                  <TableHead className="font-semibold text-gray-800 px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <UserIcon className="h-4 w-4" />
                                      Name
                                    </div>
                                  </TableHead>
                                  <TableHead className="font-semibold text-gray-800 px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <Mail className="h-4 w-4" />
                                      Email
                                    </div>
                                  </TableHead>
                                  <TableHead className="font-semibold text-gray-800 px-4 py-3">
                                    <button
                                      onClick={() => {
                                        if (sortBy === 'last_activity') {
                                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                                        } else {
                                          setSortBy('last_activity')
                                          setSortOrder('desc')
                                        }
                                      }}
                                      className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer"
                                    >
                                      <Clock className="h-4 w-4" />
                                      Last Activity
                                      {sortBy === 'last_activity' && (
                                        <span className="text-xs">
                                          {sortOrder === 'asc' ? '↑' : '↓'}
                                        </span>
                                      )}
                                    </button>
                                  </TableHead>
                                  <TableHead className="font-semibold text-gray-800 px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <Edit className="h-4 w-4" />
                                      Role
                                    </div>
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {users.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={4} className="text-center py-8">
                                      <div className="flex flex-col items-center gap-2">
                                        <Users className="h-8 w-8 text-gray-400" />
                                        <p className="text-gray-500">No users found</p>
                                        <p className="text-gray-400 text-xs">Users will appear after sign in</p>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  users
                                    .filter(user => {
                                      // Name filter
                                      const name = (user.name || 'Unknown User').toLowerCase()
                                      const nameMatch = nameFilter === '' || name.includes(nameFilter.toLowerCase())

                                      // Role filter
                                      const roleMatch = roleFilter === 'all' ||
                                        (roleFilter === 'admin' && user.is_admin) ||
                                        (roleFilter === 'user' && !user.is_admin)

                                      return nameMatch && roleMatch
                                    })
                                    .sort((a, b) => {
                                      if (sortBy === 'last_activity') {
                                        const aTime = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0
                                        const bTime = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0
                                        return sortOrder === 'asc' ? aTime - bTime : bTime - aTime
                                      } else if (sortBy === 'name') {
                                        const aName = (a.name || 'Unknown User').toLowerCase()
                                        const bName = (b.name || 'Unknown User').toLowerCase()
                                        return sortOrder === 'asc' 
                                          ? aName.localeCompare(bName)
                                          : bName.localeCompare(aName)
                                      } else if (sortBy === 'email') {
                                        const aEmail = (a.email || '').toLowerCase()
                                        const bEmail = (b.email || '').toLowerCase()
                                        return sortOrder === 'asc'
                                          ? aEmail.localeCompare(bEmail)
                                          : bEmail.localeCompare(aEmail)
                                      }
                                      return 0
                                    })
                                    .map((user, index) => (
                                    <TableRow key={user.id} className="hover:bg-blue-25 group transition-all duration-200">
                                      <TableCell className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                          <UserIcon className="h-4 w-4 text-gray-500" />
                                          <div>
                                            <p className="font-medium text-gray-900 text-sm">
                                              {user.name || 'Unknown User'}
                                            </p>
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell className="px-4 py-3">
                                        <span className={`text-sm ${
                                          user.email ? 'text-gray-900' : 'text-orange-600 font-medium'
                                        }`}>
                                          {user.email || 'TBD'}
                                        </span>
                                      </TableCell>
                                      <TableCell className="px-4 py-3">
                                        <div className="text-sm text-gray-700">
                                          {user.last_activity_at ? (
                                            <div>
                                              <div className="font-medium text-gray-900">
                                                {new Date(user.last_activity_at).toLocaleDateString('en-US', {
                                                  month: 'long',
                                                  day: 'numeric',
                                                  year: 'numeric'
                                                })}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                {new Date(user.last_activity_at).toLocaleTimeString('en-US', {
                                                  hour: 'numeric',
                                                  minute: '2-digit',
                                                  hour12: true
                                                })}
                                              </div>
                                            </div>
                                          ) : (
                                            <span className="text-gray-500">No activity</span>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell className="px-4 py-3">
                                        <div>
                                          <select
                                            value={user.access_level || 'User'}
                                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                            disabled={isUpdating}
                                            className={`px-2 py-1 border rounded text-xs transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                              isUpdating ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'
                                            }`}
                                          >
                                            <option value="Admin">👑 Admin</option>
                                            <option value="User">👤 User</option>
                                          </select>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Sign-in Logs Tab */}
                  {activeTab === 'people' && (
                    <div className="space-y-8">
                      {/* Header Section */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <Activity className="h-8 w-8 text-blue-600" />
                            Login Activity
                          </h1>
                          <p className="text-gray-600 mt-2">Monitor user authentication events and session activity</p>
                        </div>
                      </div>

                      {/* Enhanced Activity Stats */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Users className="h-5 w-5 text-blue-600" />
                                  <span className="text-sm font-semibold text-blue-800">Total Users</span>
                                </div>
                                <p className="text-3xl font-bold text-blue-900">{users.length}</p>
                              </div>
                              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <Users className="h-6 w-6 text-blue-600" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Shield className="h-5 w-5 text-purple-600" />
                                  <span className="text-sm font-semibold text-purple-800">Admin Users</span>
                                </div>
                                <p className="text-3xl font-bold text-purple-900">
                                  {users.filter(u => u.is_admin).length}
                                </p>
                              </div>
                              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <Shield className="h-6 w-6 text-purple-600" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Enhanced Login Activity Log */}
                      <Card className="shadow-sm">
                        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
                          <div className="flex items-center gap-3">
                            <Clock className="h-5 w-5 text-gray-600" />
                            <div>
                              <CardTitle className="text-xl text-gray-900">Recent Authentication Events</CardTitle>
                              <CardDescription className="mt-1">Real-time log of user login activity sorted by most recent</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-0">
                          <div className="max-h-[500px] overflow-y-auto">
                            {recentSessions.length === 0 ? (
                              <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                  <p className="text-gray-500 text-lg">No user activity found</p>
                                  <p className="text-gray-400 text-sm">Users will appear here after they sign in</p>
                                </div>
                              </div>
                            ) : (
                              (() => {
                                // Group sessions by user_id and keep only the most recent login (authentication) for each user
                                // Login Activity should show authentication events, so use started_at (login time)
                                const userLatestSessions = new Map<number, any>()
                                recentSessions.forEach(session => {
                                  const userId = session.user_id || session.user?.id
                                  if (!userId) return
                                  
                                  const existing = userLatestSessions.get(userId)
                                  // Use started_at (login/authentication time)
                                  const currentTime = new Date(session.started_at || 0).getTime()
                                  
                                  if (!existing) {
                                    userLatestSessions.set(userId, session)
                                  } else {
                                    const existingTime = new Date(existing.started_at || 0).getTime()
                                    if (currentTime > existingTime) {
                                      userLatestSessions.set(userId, session)
                                    }
                                  }
                                })
                                
                                // Convert map to array and sort by most recent login (started_at)
                                return Array.from(userLatestSessions.values())
                                  .sort((a, b) => {
                                    const aTime = new Date(a.started_at || 0).getTime()
                                    const bTime = new Date(b.started_at || 0).getTime()
                                    return bTime - aTime
                                  })
                                  .map((session, index) => (
                                  <div
                                    key={session.session_id || session.id}
                                    className={`group relative transition-all duration-200 hover:bg-blue-25 border-b border-gray-100 last:border-b-0 ${
                                      index === 0 ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between p-2">
                                      {/* User Info Section */}
                                      <div className="flex items-center gap-4 flex-1">
                                        {/* Status Indicator */}
                                        <div className="relative">
                                          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 ring-4 ring-gray-50">
                                            <UserIcon className="h-6 w-6 text-gray-500" />
                                          </div>
                                        </div>

                                        {/* User Details */}
                                        <div className="flex-1">
                                          <div className="flex items-center gap-3 mb-2">
                                            <h3 className="font-semibold text-gray-900 text-lg">
                                              {session.user?.name || 'Unknown User'}
                                            </h3>
                                            <Badge
                                              variant={session.user?.is_admin ? 'default' : 'outline'}
                                              className={session.user?.is_admin
                                                ? 'bg-purple-100 text-purple-800 border-purple-200'
                                                : 'text-gray-600 border-gray-300'
                                              }
                                            >
                                              {session.user?.is_admin ? '👑 Admin' : '👤 User'}
                                            </Badge>
                                            {index === 0 && (
                                              <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                                                ⚡ Most Recent
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <div className="flex items-center gap-2">
                                              <Mail className="h-4 w-4" />
                                              <span className={session.user?.email ? 'text-gray-700' : 'text-orange-600 font-medium'}>
                                                {session.user?.email || '📧 TBD'}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Timestamp & Status Section */}
                                      <div className="text-right space-y-2">
                                        <div className="flex items-center gap-2 justify-end">
                                          <Calendar className="h-4 w-4 text-gray-500" />
                                          <span className="text-sm font-medium text-gray-900">
                                            {session.started_at
                                              ? new Date(session.started_at).toLocaleDateString('en-US', {
                                                  month: 'short',
                                                  day: 'numeric',
                                                  year: 'numeric'
                                                })
                                              : 'No activity'
                                            }
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-2 justify-end">
                                          <Clock className="h-4 w-4 text-gray-500" />
                                          <span className="text-xs text-gray-500">
                                            {session.started_at
                                              ? new Date(session.started_at).toLocaleTimeString('en-US', {
                                                  hour: 'numeric',
                                                  minute: '2-digit',
                                                  hour12: true
                                                })
                                              : '---'
                                            }
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              })()
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* My Account Tab */}
                  {activeTab === 'my-account' && (
                    <div className="space-y-6">
                      <h1 className="text-2xl font-bold text-gray-900">My Account</h1>

                      <Card>
                        <CardHeader>
                          <CardTitle>Profile Information</CardTitle>
                          <CardDescription>Your account details and settings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Name</label>
                            <p className="text-sm text-gray-900">{currentUser?.name || 'Not set'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Email</label>
                            <p className="text-sm text-gray-900">
                              {currentUser?.email ||
                               currentUser?.preferred_username ||
                               currentUser?.unique_name ||
                               currentUser?.upn ||
                               'ko.admin@giatec.ca'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Role</label>
                            <p className="text-sm text-gray-900">
                              {currentUser?.access_level || (currentUser?.is_admin ? 'Admin' : 'User')}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Popular Templates Tab */}
                  {activeTab === 'popular-templates' && (
                    <div className="space-y-6">
                      <div className="pr-20">
                        <h1 className="text-2xl font-bold text-gray-900">Popular Templates</h1>
                      </div>

                      <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-500 rounded-lg">
                                <Crown className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <CardTitle className="text-blue-800">Most Used Public Templates</CardTitle>
                                <CardDescription className="text-blue-600">
                                  Public templates ranked by usage frequency
                                </CardDescription>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-sm text-gray-500">
                                {templates.length} public templates
                              </span>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {isLoading ? (
                            <div className="flex items-center justify-center h-32">
                              <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                            </div>
                          ) : templates.length === 0 ? (
                            <div className="text-center py-8">
                              <Crown className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <p className="text-gray-500">No public templates found</p>
                              <p className="text-sm text-gray-400 mt-1">
                                Public templates will appear here once created and used
                              </p>
                            </div>
                          ) : (
                            <div className="grid gap-4">
                              {templates.map((template, index) => (
                                <div
                                  key={template.id}
                                  className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-all duration-200 hover:border-blue-300"
                                >
                                  <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                        index === 0 ? 'bg-yellow-100 text-yellow-800' :
                                        index === 1 ? 'bg-gray-100 text-gray-800' :
                                        index === 2 ? 'bg-orange-100 text-orange-800' :
                                        'bg-blue-100 text-blue-800'
                                      }`}>
                                        {index + 1}
                                      </div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                          <h3 className="font-semibold text-gray-900 truncate">
                                            {template.title}
                                          </h3>
                                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                            {template.description || 'No description provided'}
                                          </p>
                                          <div className="flex items-center gap-4 mt-3">
                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                              <span className={`inline-block w-2 h-2 rounded-full ${
                                                template.type === 'prompt' ? 'bg-green-500' : 'bg-purple-500'
                                              }`} />
                                              {template.type}
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                                              <Users className="h-3 w-3" />
                                              Used {template.usage_count || 0} times
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                              <Clock className="h-3 w-3" />
                                              {template.created_at
                                                ? new Date(template.created_at).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                  })
                                                : 'Unknown date'
                                              }
                                            </div>
                                          </div>

                                          {template.keywords && template.keywords.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                              {template.keywords.slice(0, 3).map((keyword, keywordIndex) => (
                                                <span
                                                  key={keywordIndex}
                                                  className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                                                >
                                                  {keyword}
                                                </span>
                                              ))}
                                              {template.keywords.length > 3 && (
                                                <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                                  +{template.keywords.length - 3} more
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>

                                        <div className="flex items-center gap-2">
                                          <div className="text-right">
                                            <div className="text-lg font-bold text-blue-600">
                                              {template.usage_count || 0}
                                            </div>
                                            <p className="text-xs text-gray-500">
                                              uses
                                            </p>
                                          </div>

                                          {index < 3 && (
                                            <Crown className={`h-5 w-5 ${
                                              index === 0 ? 'text-yellow-500' :
                                              index === 1 ? 'text-gray-400' :
                                              'text-orange-400'
                                            }`} />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Popular Models Tab */}
                  {activeTab === 'popular-models' && (
                    <div className="space-y-6">
                      <div className="pr-20">
                        <h1 className="text-2xl font-bold text-gray-900">Popular Models</h1>
                        <p className="text-sm text-gray-500 mt-1">
                          Model usage statistics based on conversation messages
                        </p>
                      </div>

                      {/* Date Range Selector */}
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-4 flex-wrap">
                            <label className="text-sm font-medium text-gray-700">Time Range:</label>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setDateRange('7d')}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                  dateRange === '7d'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                7 Days
                              </button>
                              <button
                                onClick={() => setDateRange('30d')}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                  dateRange === '30d'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                30 Days
                              </button>
                              <button
                                onClick={() => setDateRange('90d')}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                  dateRange === '90d'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                90 Days
                              </button>
                              <button
                                onClick={() => setDateRange('custom')}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                  dateRange === 'custom'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                Custom
                              </button>
                            </div>
                            
                            {dateRange === 'custom' && (
                              <div className="flex items-center gap-2">
                                <input
                                  type="date"
                                  value={customStartDate}
                                  onChange={(e) => setCustomStartDate(e.target.value)}
                                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                                />
                                <span className="text-gray-500">to</span>
                                <input
                                  type="date"
                                  value={customEndDate}
                                  onChange={(e) => setCustomEndDate(e.target.value)}
                                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                                />
                              </div>
                            )}
                            
                            <button
                              onClick={loadPopularModels}
                              disabled={modelsLoading}
                              className="ml-auto px-4 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              <RefreshCw className={`h-4 w-4 ${modelsLoading ? 'animate-spin' : ''}`} />
                              Refresh
                            </button>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Bar Chart */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Model Usage Statistics</CardTitle>
                          <CardDescription>
                            Number of messages generated by each model
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {modelsLoading ? (
                            <div className="flex items-center justify-center h-64">
                              <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                            </div>
                          ) : popularModels.length === 0 ? (
                            <div className="text-center py-12">
                              <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <p className="text-gray-500">No model usage data available</p>
                              <p className="text-sm text-gray-400 mt-1">
                                Model usage will appear here once conversations are created
                              </p>
                            </div>
                          ) : (
                            <BarChart data={popularModels} />
                          )}
                        </CardContent>
                      </Card>

                      {/* Model List Table */}
                      {popularModels.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle>Model Usage Details</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Rank</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead className="text-right">Usage Count</TableHead>
                                    <TableHead className="text-right">Percentage</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {popularModels.map((model, index) => {
                                    const total = popularModels.reduce((sum, m) => sum + m.usage_count, 0)
                                    const percentage = total > 0 ? ((model.usage_count / total) * 100).toFixed(1) : '0.0'
                                    return (
                                      <TableRow key={model.model_id}>
                                        <TableCell>
                                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                            index === 0 ? 'bg-yellow-100 text-yellow-800' :
                                            index === 1 ? 'bg-gray-100 text-gray-800' :
                                            index === 2 ? 'bg-orange-100 text-orange-800' :
                                            'bg-blue-100 text-blue-800'
                                          }`}>
                                            {index + 1}
                                          </div>
                                        </TableCell>
                                        <TableCell className="font-medium">{model.model_label}</TableCell>
                                        <TableCell className="text-right">{model.usage_count.toLocaleString()}</TableCell>
                                        <TableCell className="text-right">{percentage}%</TableCell>
                                      </TableRow>
                                    )
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Analytics Tab */}
                  {activeTab === 'analytics' && (
                    <AnalyticsDashboard />
                  )}

                  {/* Integrations Tab - Placeholder Content */}
                  {activeTab === 'integrations' && (
                    <div className="space-y-6 h-full">
                      <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
                      <Card className="h-96">
                        <CardHeader>
                          <CardTitle>Integration Management</CardTitle>
                          <CardDescription>Manage your integration settings</CardDescription>
                        </CardHeader>
                        <CardContent className="flex items-center justify-center h-64">
                          <p className="text-gray-600">This section is coming soon...</p>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}